import React from "react";
import styled from "styled-components/macro";
import { LabelType } from "../types";
import { ImWrench } from "react-icons/im";
import { noSelect } from "theme/GlobalElements";

interface LabelProps {
  text: string;
  color: string;
  textColor: string;
  labelType: LabelType;
  muted?: boolean;
}

const parseHexColor = (color: string): [number, number, number] | null => {
  const value = color.trim().replace(/^#/, "");
  const normalized =
    value.length === 3
      ? value
          .split("")
          .map((character) => character + character)
          .join("")
      : value;

  if (!/^[0-9a-fA-F]{6}$/.test(normalized)) {
    return null;
  }

  return [
    parseInt(normalized.slice(0, 2), 16),
    parseInt(normalized.slice(2, 4), 16),
    parseInt(normalized.slice(4, 6), 16),
  ];
};

const getRelativeLuminance = ([red, green, blue]: [
  number,
  number,
  number
]): number => {
  const [r, g, b] = [red, green, blue].map((channel) => {
    const value = channel / 255;
    return value <= 0.03928
      ? value / 12.92
      : Math.pow((value + 0.055) / 1.055, 2.4);
  });

  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

const getContrastRatio = (
  first: [number, number, number],
  second: [number, number, number]
): number => {
  const firstLuminance = getRelativeLuminance(first);
  const secondLuminance = getRelativeLuminance(second);
  const lighter = Math.max(firstLuminance, secondLuminance);
  const darker = Math.min(firstLuminance, secondLuminance);

  return (lighter + 0.05) / (darker + 0.05);
};

export const getReadableTextColor = (
  backgroundColor: string,
  preferredTextColor: string
): string => {
  const background = parseHexColor(backgroundColor);
  const preferred = parseHexColor(preferredTextColor);

  if (!background) {
    return preferredTextColor;
  }

  if (preferred && getContrastRatio(background, preferred) >= 4) {
    return preferredTextColor;
  }

  const darkText = "#191919";
  const lightText = "#FFFFFF";
  const darkContrast = getContrastRatio(background, parseHexColor(darkText)!);
  const lightContrast = getContrastRatio(
    background,
    parseHexColor(lightText)!
  );

  return darkContrast >= lightContrast ? darkText : lightText;
};

const mixColor = (
  source: [number, number, number],
  target: [number, number, number],
  targetWeight: number
): [number, number, number] =>
  source.map((channel, index) =>
    Math.round(channel * (1 - targetWeight) + target[index] * targetWeight)
  ) as [number, number, number];

const rgbToHex = ([red, green, blue]: [
  number,
  number,
  number
]): string =>
  `#${[red, green, blue]
    .map((channel) => channel.toString(16).padStart(2, "0"))
    .join("")}`.toUpperCase();

export const getMutedBadgePalette = (
  backgroundColor: string,
  preferredTextColor: string = "#191919"
) => {
  const source = parseHexColor(backgroundColor);

  if (!source) {
    return {
      backgroundColor,
      borderColor: "transparent",
      textColor: getReadableTextColor(backgroundColor, preferredTextColor),
    };
  }

  const isVividRed = source[0] >= 220 && source[1] <= 90 && source[2] <= 90;
  const isVividLime =
    source[0] >= 170 && source[1] >= 220 && source[2] <= 90;

  if (!isVividRed && !isVividLime) {
    return {
      backgroundColor,
      borderColor: "transparent",
      textColor: getReadableTextColor(backgroundColor, preferredTextColor),
    };
  }

  const neutral: [number, number, number] = [128, 128, 128];
  const tonedBackground = rgbToHex(mixColor(source, neutral, 0.16));

  return {
    backgroundColor: tonedBackground,
    borderColor: "transparent",
    textColor: getReadableTextColor(tonedBackground, preferredTextColor),
  };
};

const Label: React.FC<LabelProps> = ({
  text,
  color,
  textColor,
  labelType,
  muted = false,
}) => {
  color = color || "#D8FF00";
  textColor = textColor || "black";
  const palette = muted
    ? getMutedBadgePalette(color, textColor)
    : {
        backgroundColor: color,
        borderColor: "transparent",
        textColor: getReadableTextColor(color, textColor),
      };

  return (
    <LabelContainer
      style={{
        backgroundColor: palette.backgroundColor,
        borderColor: palette.borderColor,
        color: palette.textColor,
      }}
    >
      {labelType && labelType === LabelType.Warranty && (
        <ImWrench style={{ width: 14, height: 14, fill: "currentColor" }} />
      )}
      <Text className="text-sm" label={labelType}>
        {text}
      </Text>
    </LabelContainer>
  );
};

const LabelContainer = styled.span`
  display: flex;
  flex-flow: row nowrap;
  justify-content: center;
  align-items: center;
  padding: 5px 5px;
  border: 1px solid transparent;
  border-radius: 6px;
  margin-right: 5px;
  &:last-child {
    margin-right: 0;
  }
  margin-bottom: 5px;
`;

const Text = styled.p<{ label: LabelType }>`
  margin: 0;
  font-weight: 600;
  margin-left: ${(props) =>
    props.label === LabelType.Warranty ? "5px" : "0px"};
  ${noSelect};
`;

export default Label;
